/**
 * The two questions the editor asks before it does something the trainer cannot undo from here.
 *
 * `ConfirmDialog` words nothing — every string arrives on the prompt, already resolved — so this is
 * where the consequence is stated. Both name something the trainer cannot see from the surface they
 * are standing on: a delete stops every assigned client receiving the form while keeping everything
 * they have already sent (R26), and leaving with unsaved edits loses them outright, because this app
 * keeps no draft anywhere.
 *
 * `t` arrives as a parameter — this is `model/`, and it imports no i18next.
 */

import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import type { EditorDraft } from './editorDraft';
import type { TranslateKey } from './translateKey';

/**
 * „Usuń ankietę", or `null` for the form that cannot be deleted.
 *
 * The description carries R26's promise rather than a warning: the icon says destruction and the
 * truth is milder than it looks, so a trainer who is not told will assume their clients' answers go
 * with it.
 *
 * R26's other half is the `null`: the intake cannot be deleted at all. The editor's ⋯ is suppressed
 * for it, so nothing can reach this question — but the refusal belongs where it can be tested rather
 * than in the one component that happens to draw the menu.
 */
export function deleteSurveyPrompt(
  draft: EditorDraft,
  t: TranslateKey,
  busy: boolean,
): ConfirmPrompt | null {
  // The lead form cannot be deleted either: it belongs to the public form and goes with it.
  if (draft.kind !== 'checkIn') return null;
  return {
    title: t('web:reports.editor.delete.title'),
    // Not interpolated: Polish declines a survey's name inside the sentence and the app cannot
    // inflect one. On `subject` it stays in the nominative, which is always correct.
    subject: draft.title,
    description: t('web:reports.editor.delete.body'),
    confirmLabel: t('web:reports.editor.delete.confirm'),
    cancelLabel: t('web:reports.editor.delete.cancel'),
    destructiveConfirm: true,
    busy,
  };
}

/**
 * The leave guard: three answers, because two would force a choice nobody wants to make.
 *
 * „Zapisz i wyjdź" is the alternate rather than the confirm, so the answer that DESTROYS work is the
 * one drawn in `--danger` and the safe one is not the default press. The same disposition the plan
 * builder's guard takes, for the same reason.
 *
 * `saveRefused` is the third answer's own refusal — a form with no name, a cadence with no day. The
 * sentence is rendered on the card rather than left to a `title`, because the copy of it that stands
 * beside the save button is behind this dialog's scrim, and an answer that does nothing when pressed
 * with no visible reason reads as a broken screen.
 */
export function leaveEditorPrompt(
  t: TranslateKey,
  busy: boolean,
  saveRefused: string | null,
): ConfirmPrompt {
  return {
    title: t('web:reports.editor.leave.title'),
    description: t('web:reports.editor.leave.body'),
    confirmLabel: t('web:reports.editor.leave.discard'),
    cancelLabel: t('web:reports.editor.leave.stay'),
    alternateLabel: t('web:reports.editor.leave.save'),
    ...(saveRefused === null ? {} : { alternateRefusal: saveRefused }),
    destructiveConfirm: true,
    busy,
  };
}

/**
 * A save that lost a race: the form was saved somewhere else — another tab, or the same questions
 * in the other editor (public-form R4) — after this one was opened, and the server answered 409.
 *
 * Two answers and no third. The CONFIRM is the one that throws the trainer's edits away, so it is
 * drawn as the danger; Cancel (and Escape) keeps them, rebuilt on the server's copy, and the next
 * save carries them. Nothing is written by either answer — which is why neither needs a busy state
 * beyond the read that fetches the server's copy.
 */
export function conflictPrompt(t: TranslateKey, busy: boolean): ConfirmPrompt {
  return {
    title: t('web:reports.editor.conflict.title'),
    description: t('web:reports.editor.conflict.body'),
    confirmLabel: t('web:reports.editor.conflict.reload'),
    cancelLabel: t('web:reports.editor.conflict.keep'),
    destructiveConfirm: true,
    busy,
  };
}
